import { Box } from '@bonanza-social/moonlight';
import { memo, useEffect } from 'react';
import Svg, { Circle } from 'react-native-svg';
import Animated, {
  useSharedValue,
  useAnimatedProps,
  withTiming,
  withDelay,
  Easing,
} from 'react-native-reanimated';
import StatusLinearSvgGradient from '../StatusLinearSvgGradient/StatusLinearSvgGradient';
import { StatusCircularProgressProps } from './StatusCircularProgress.types';
import StatusGemShowcase from '../StatusGemShowcase/StatusGemShowcase';
import StatusGemWithOutline from '../StatusGemWithOutline/StatusGemWithOutline';

const AnimatedCircle = Animated.createAnimatedComponent(Circle);

function StatusCircularProgress({
  currentPoints,
  totalPoints,
  playerStatus,
  size = 254,
  strokeWidth = 8,
  gapDegrees = 30,
}: StatusCircularProgressProps) {
  const radius = (size * 70) / 160;
  const center = size / 2;
  const fullArc = 2 * Math.PI * radius;
  const gapRadians = (gapDegrees * Math.PI) / 180;
  const usableArc = fullArc - gapRadians * radius;
  const animatedProgress = useSharedValue(0);

  // Animate progress from 0 to currentPoints on mount
  useEffect(() => {
    animatedProgress.value = withDelay(
      200, // delay to allow bottom sheet to open
      withTiming(currentPoints, {
        duration: 2000,
        easing: Easing.out(Easing.exp),
      }),
    );
  }, [currentPoints, animatedProgress]);

  const progressCircleAnimation = useAnimatedProps(() => {
    const progress = animatedProgress.value / totalPoints;
    const progressArcLength = usableArc * progress;
    return {
      strokeDasharray: `${progressArcLength} ${fullArc}`,
    };
  });

  return (
    <Box position="relative">
      <Box
        position="absolute"
        top="-space-100"
        left="space-0"
        right="space-0"
        alignItems="center"
        zIndex={1}
      >
        <StatusGemWithOutline
          playerStatus={playerStatus}
          size="space-1000"
          outlineColor="iconOutline"
        />
      </Box>
      <StatusGemShowcase
        playerStatus={playerStatus}
        progressRingSize={size}
        currentPoints={currentPoints}
        totalPoints={totalPoints}
      />
      <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* register svg gradient for the current status */}
        <StatusLinearSvgGradient statusName={playerStatus} />
        {/* background ring */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke="#000000f7"
          strokeWidth={strokeWidth}
          strokeDasharray={`${usableArc} ${fullArc}`}
          strokeLinecap="round"
          fill="none"
          transform={`rotate(-${90 - gapDegrees} ${center} ${center})`}
        />
        {/* progress ring  */}
        {/* if currentPoints is 0 there is still a small amount of stroke being rendered so we return null instead */}
        {currentPoints ? (
          <AnimatedCircle
            cx={center}
            cy={center}
            r={radius}
            stroke={`url(#${playerStatus})`}
            strokeWidth={strokeWidth}
            fill="none"
            strokeLinecap="round"
            transform={`rotate(-${90 - gapDegrees} ${center} ${center})`}
            animatedProps={progressCircleAnimation}
          />
        ) : null}
      </Svg>
    </Box>
  );
}

export default memo(StatusCircularProgress);
